// Formatos de saída do Studio. Todos saem da mesma vista (Princípio IV): escolher ou gerar um
// formato só troca o template, nunca chama modelo nem refaz a análise.
import { useSearchParams } from "react-router";

export type IdFormato = "card" | "didatico" | "numeros" | "encaminhar" | "imagem" | "audio";

export interface Formato {
  id: IdFormato;
  nome: string;
  descricao: string;
  icone: string;
  /** Ainda não existe; aparece desabilitado com "em breve". */
  emBreve?: boolean;
}

export const FORMATOS: readonly Formato[] = [
  { id: "card", nome: "Card", descricao: "Tudo em blocos, com as fontes.", icone: "▤" },
  {
    id: "didatico",
    nome: "Passo a passo",
    descricao: "Explicado em etapas, com régua do dado e um teste rápido.",
    icone: "✎",
  },
  {
    id: "numeros",
    nome: "Com números",
    descricao: "Tabela simples da série oficial.",
    icone: "▦",
  },
  {
    id: "encaminhar",
    nome: "Para encaminhar",
    descricao: "Texto curto para mandar no WhatsApp.",
    icone: "✉",
  },
  {
    id: "imagem",
    nome: "Card-imagem",
    descricao: "Imagem com data e aviso de IA, pronta para compartilhar.",
    icone: "▣",
  },
  { id: "audio", nome: "Áudio", descricao: "Narração do card.", icone: "♪", emBreve: true },
];

const PADRAO: IdFormato[] = ["card"];
const ids = new Set<string>(FORMATOS.filter((f) => !f.emBreve).map((f) => f.id));

export function formato(id: IdFormato): Formato {
  const f = FORMATOS.find((x) => x.id === id);
  if (!f) throw new Error(`formato desconhecido: ${id}`);
  return f;
}

export function lerFormatos(valor: string | null): IdFormato[] {
  const lista = (valor ?? "").split(",").filter((v): v is IdFormato => ids.has(v));
  return lista.length ? [...new Set(lista)] : PADRAO;
}

/**
 * Formatos escolhidos e o que está aberto, guardados na URL (`?formatos=card,didatico&ver=…`),
 * para o link do resultado abrir igual e o voltar do navegador funcionar.
 */
export function useFormatos(disponiveis?: readonly IdFormato[]) {
  const [busca, setBusca] = useSearchParams();
  const todos = lerFormatos(busca.get("formatos"));
  const escolhidos = disponiveis ? todos.filter((f) => disponiveis.includes(f)) : todos;
  const lista = escolhidos.length ? escolhidos : PADRAO;
  const pedido = busca.get("ver") as IdFormato | null;
  const ativo = pedido && lista.includes(pedido) ? pedido : (lista[0] ?? "card");

  const gravar = (novos: IdFormato[], ver?: IdFormato) => {
    setBusca(
      (atual) => {
        const proxima = new URLSearchParams(atual);
        proxima.set("formatos", (novos.length ? novos : PADRAO).join(","));
        if (ver) proxima.set("ver", ver);
        return proxima;
      },
      { replace: true, preventScrollReset: true },
    );
  };

  return {
    escolhidos: lista,
    ativo,
    alternar: (id: IdFormato) => {
      gravar(lista.includes(id) ? lista.filter((f) => f !== id) : [...lista, id]);
    },
    abrir: (id: IdFormato) => {
      gravar(lista.includes(id) ? lista : [...lista, id], id);
    },
  };
}
